import { useEffect, useState, useCallback, useRef } from 'react';
import { useAppDispatch } from '../app/hooks';
import { setAccount, clearAccount, disconnectWalletState } from '../data/accountSlice';
import { useAppKit } from '@reown/appkit/react';
import { useAccount, useDisconnect, useChainId, useSwitchChain, useSignMessage } from 'wagmi';
import { BSC_NETWORKS } from '../config/networks';

export const useWalletConnect = () => {
  const dispatch = useAppDispatch();
  const { open } = useAppKit();
  const { address, isConnected, status } = useAccount();
  const chainIdHook = useChainId();
  const { disconnect } = useDisconnect();
  const { switchChainAsync } = useSwitchChain();
  const { signMessageAsync } = useSignMessage();

  const [isConnecting, setIsConnecting] = useState(false);
  const prevStatusRef = useRef<string>(status);

  useEffect(() => {
     setIsConnecting(status === 'connecting' || status === 'reconnecting');
  }, [status]);

  useEffect(() => {
    if (status === 'connected' && address) {
      dispatch(setAccount({ address, isConnected: true, chainId: chainIdHook || 0 }));
    } else if (status === 'disconnected') {
      // If we were previously connected and now disconnected, it's likely a manual disconnect (or wallet lock)
      // In this case, we should clear the session (Logout).
      if (prevStatusRef.current === 'connected') {
        dispatch(clearAccount());
      } else {
        // Otherwise (e.g. initial load), just sync the state without clearing token
        dispatch(disconnectWalletState());
      }
    }
    
    prevStatusRef.current = status;
  }, [status, address, chainIdHook, dispatch]);

  const connectWallet = useCallback(async () => {
    try {
      // If already connected, open the Account view, otherwise open Connect view
      // Actually, passing no arguments usually lets AppKit decide best view
      await open(); 
    } catch (error) {
      console.error('[WalletConnect] Failed to open modal', error);
    }
  }, [open]);

  const connectWalletWithQr = useCallback(async () => {
    // Reown AppKit handles QR automatically in the Connect view
    await connectWallet();
  }, [connectWallet]);

  const disconnectWallet = useCallback(async () => {
    try {
      await disconnect();
      // Explicitly clear account (including token) on manual disconnect
      dispatch(clearAccount());
    } catch (error) {
      console.error('[WalletConnect] Disconnect failed', error);
    }
  }, [disconnect, dispatch]);

  const switchNetwork = useCallback(async (targetChainId: number) => {
    try {
      if (switchChainAsync) {
        await switchChainAsync({ chainId: targetChainId });
      } else {
        const network = targetChainId === 56 ? BSC_NETWORKS.mainnet : BSC_NETWORKS.testnet;
        console.warn('Switch network not available:', network.chainName);
      }
    } catch (error) {
      console.error('[WalletConnect] Switch network failed', error);
    }
  }, [switchChainAsync]);

  const signMessage = useCallback(async (message: string) => {
    try {
      return await signMessageAsync({ message });
    } catch (error) {
      console.error('[WalletConnect] Sign message failed', error);
      throw error;
    }
  }, [signMessageAsync]);

  return {
    connector: null,
    account: address || '',
    chainId: chainIdHook || 0,
    isConnected,
    isConnecting,
    connectWallet,
    connectWalletWithQr,
    disconnectWallet,
    switchNetwork,
    signMessage,
  };
};
